import React from 'react';
import type { SalesOrder } from '@/services/salesOrder.service';
import type {
  SalesOrderTimesheet,
  TimesheetBillingPreview,
} from '@/services/salesOrderTimesheet.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

export interface PrintableSalesOrderTimesheetProps {
  order: SalesOrder;
  timesheet: SalesOrderTimesheet;
  preview?: TimesheetBillingPreview | null;
  companyInfo?: {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    taxNumber?: string;
  };
  onClose?: () => void;
}

function periodLabel(year: number, month: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function wtDays(person: { days?: Array<{ attendance_code: string }> }) {
  return (person.days || []).filter((d) =>
    ['W', 'T'].includes(String(d.attendance_code).toUpperCase())
  ).length;
}

/** Sales-order timesheet print — people + billing preview via commercial A4 engine. */
export const PrintableSalesOrderTimesheet: React.FC<PrintableSalesOrderTimesheetProps> = ({
  order,
  timesheet,
  preview,
  companyInfo,
  onClose,
}) => {
  const currency = order.currency || 'AED';
  const period = periodLabel(timesheet.period_year, timesheet.period_month);
  const people = [...(timesheet.people || [])].sort(
    (a, b) => (a.line_number ?? 0) - (b.line_number ?? 0)
  );
  const billableLines = (preview?.lines || []).filter((l) => l.attendance_exists);
  const previewTotal = billableLines.reduce((s, l) => s + Number(l.proposed_line_amount || 0), 0);

  const personLines = people.length
    ? people.map((p, idx) => ({
        lineNumber: p.line_number ?? idx + 1,
        description: p.person_name || '—',
        cells: {
          sno: String(p.line_number ?? idx + 1),
          name: p.person_name || '—',
          position: p.position || '—',
          badge: p.badge_id || '—',
          wt: String(wtDays(p)),
        },
      }))
    : [
        {
          lineNumber: 1,
          description: 'No people',
          cells: { sno: '1', name: '—', position: '—', badge: '—', wt: '0' },
        },
      ];

  return (
    <EngineCommercialA4Print
      docType="sales_order_timesheet"
      title="CUSTOMER ORDER TIMESHEET"
      documentNumber={`${order.sales_order_number || 'SO'}-${period}`}
      partyName={order.customer_name || ''}
      letterhead
      onClose={onClose}
      introText={
        'Attendance summary for timesheet-fulfillment customer orders. ' +
        'Invoice quantity = people with ≥1 W/T day per position; unit price = monthly rate × calendar prorata.'
      }
      metaLines={[
        { label: 'Customer', value: order.customer_name ? `M/s. ${order.customer_name}` : '' },
        { label: 'Customer Order', value: order.sales_order_number || '' },
        { label: 'Period', value: period },
        { label: 'Status', value: timesheet.status || '' },
        { label: 'People', value: String(people.length) },
        { label: 'Calendar days', value: String(preview?.calendar_days ?? '') },
        { label: 'TRN', value: companyInfo?.taxNumber?.replace(/^TRN:\s*/i, '') || '' },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: 'No' },
        { key: 'name', label: 'Name' },
        { key: 'position', label: 'Position' },
        { key: 'badge', label: 'Badge' },
        { key: 'wt', label: 'W/T days', align: 'right' },
      ]}
      lines={personLines}
      finals={[
        { id: 'billing', heightPx: Math.max(96, 40 + billableLines.length * 22) },
        { id: 'sign', heightPx: 72 },
      ]}
      finalBlocks={{
        billing: (
          <div style={{ fontSize: 11 }}>
            <strong>Billing preview</strong>
            <table style={{ width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>Position</th>
                  <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>Qty</th>
                  <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>Prorata</th>
                  <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>Unit</th>
                  <th style={{ textAlign: 'right', borderBottom: '1px solid #ccc' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {billableLines.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No billable positions</td>
                  </tr>
                ) : (
                  billableLines.map((l) => (
                    <tr key={l.sales_order_item_id}>
                      <td>{l.item_description}</td>
                      <td style={{ textAlign: 'right' }}>{l.proposed_quantity}</td>
                      <td style={{ textAlign: 'right' }}>
                        {Math.round(Number(l.prorata || 0) * 10000) / 100}%
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatMoney(l.proposed_unit_price, {
                          ...PRINT_PLAIN_AMOUNT_PROFILE,
                          currency,
                        })}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatMoney(l.proposed_line_amount, {
                          ...PRINT_PLAIN_AMOUNT_PROFILE,
                          currency,
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <strong>
                Preview total (ex-VAT):{' '}
                {formatMoney(previewTotal, { ...PRINT_PLAIN_AMOUNT_PROFILE, currency })}
              </strong>
            </div>
          </div>
        ),
        sign: (
          <div style={{ display: 'flex', gap: 48, fontSize: 11 }}>
            <div>
              Prepared By
              <br />
              _______________________
            </div>
            <div>
              Approved By
              <br />
              _______________________
            </div>
          </div>
        ),
      }}
    />
  );
};

export default PrintableSalesOrderTimesheet;
